import { ms } from '@shared/util'
import { useEffect, useState } from 'react'

// The pure reminder-message helpers now live in `@shared/core/events` so the
// monolith can reuse them. Re-exported here to preserve existing import paths.
export {
  buildMessagesSentAndNext,
  hasAnyScheduledMessages,
  hasAnySentMessages,
  isEventActivating,
} from '@shared/core/events'
export type { MessageSentAndNext } from '@shared/core/events'

const TTL_MS = ms('30 seconds')
const MAX_ENTRIES = 10

const expiresAtById = new Map<string, number>()
const listeners = new Set<() => void>()
let expiryTimer: ReturnType<typeof setTimeout> | null = null

function emit() {
  listeners.forEach((listener) => listener())
}

function clearExpiryTimer() {
  if (expiryTimer !== null) {
    clearTimeout(expiryTimer)
    expiryTimer = null
  }
}

function pruneExpired(): boolean {
  const now = Date.now()
  let changed = false
  for (const [id, exp] of expiresAtById) {
    if (exp <= now) {
      expiresAtById.delete(id)
      changed = true
    }
  }
  return changed
}

function trimToMax() {
  while (expiresAtById.size > MAX_ENTRIES) {
    const oldest = expiresAtById.keys().next().value
    if (oldest === undefined) break
    expiresAtById.delete(oldest)
  }
}

function scheduleNextExpiry() {
  clearExpiryTimer()
  const now = Date.now()
  let minExpiry = Infinity
  for (const t of expiresAtById.values()) {
    if (t > now && t < minExpiry) {
      minExpiry = t
    }
  }
  if (minExpiry === Infinity) {
    return
  }
  expiryTimer = setTimeout(() => {
    expiryTimer = null
    if (pruneExpired()) {
      emit()
    }
    scheduleNextExpiry()
  }, minExpiry - now)
}

export function setEventRecentlyActivated(eventId: string) {
  pruneExpired()
  expiresAtById.delete(eventId)
  expiresAtById.set(eventId, Date.now() + TTL_MS)
  trimToMax()
  scheduleNextExpiry()
  emit()
}

export function clearEventRecentlyActivated(eventId: string) {
  if (!expiresAtById.has(eventId)) {
    return
  }
  expiresAtById.delete(eventId)
  scheduleNextExpiry()
  emit()
}

export function wasEventRecentlyActivated(eventId: string): boolean {
  pruneExpired()
  const exp = expiresAtById.get(eventId)
  return exp !== undefined && exp > Date.now()
}

function hasAnyRecentlyActivatedEvents(): boolean {
  pruneExpired()
  return expiresAtById.size > 0
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function useWasEventRecentlyActivated(eventId: string): boolean {
  const [value, setValue] = useState(() => wasEventRecentlyActivated(eventId))

  useEffect(() => {
    setValue(wasEventRecentlyActivated(eventId))
    return subscribe(() => setValue(wasEventRecentlyActivated(eventId)))
  }, [eventId])

  return value
}

export function useHasRecentlyActivatedEvents(): boolean {
  const [value, setValue] = useState(() => hasAnyRecentlyActivatedEvents())

  useEffect(() => {
    setValue(hasAnyRecentlyActivatedEvents())
    return subscribe(() => setValue(hasAnyRecentlyActivatedEvents()))
  }, [])

  return value
}
